(function () {
  const sectionSelector = "[data-collection-product-grid-section]";
  const interactiveSelectors = [
    ".collection-filter-sidebar__checkbox",
    ".collection-filter-sidebar__switch",
    ".collection-filter-pills a",
    ".collection-product-grid__mobile-filter-reset",
    ".collection-product-grid__pagination a",
  ].join(", ");
  const formSelectors = [
    ".collection-sorting-button__form",
    ".collection-filter-sidebar__price-form",
  ].join(", ");

  if (window.PlanturaCollectionProductGrid) {
    return;
  }

  const CollectionProductGrid = {
    abortController: null,
    mobileFilterAbortController: null,
    renderRequestId: 0,
    mobileFilterRenderRequestId: 0,
    mobileFilterHideTimer: null,
    mobileFilterPanelSnapshots: new WeakMap(),
    mobileFilterCompactWidthViewports: new WeakMap(),
    stickyMobileFilterFrame: null,
    stickyMobileFilterRoot: null,
    stickyMobileFilterForceCompactWidth: false,
    lastMobileFilterTrigger: null,
    suppressMobileFilterToggleCommit: false,

    init() {
      this.initializeSectionState();
      this.initializeMobileFilterPills();
      this.updateStickyMobileFilterBars();
      document.addEventListener("click", this.handleClick.bind(this));
      document.addEventListener("submit", this.handleSubmit.bind(this));
      document.addEventListener("toggle", this.handleToggle.bind(this), true);
      document.addEventListener("keydown", this.handleKeydown.bind(this));
      window.addEventListener("popstate", this.handlePopState.bind(this));
      window.addEventListener("scroll", this.handleScroll.bind(this), {
        passive: true,
      });
      window.addEventListener("resize", this.handleResize.bind(this));
    },

    getCurrentUrl() {
      return `${window.location.pathname}${window.location.search}${window.location.hash}`;
    },

    initializeSectionState(root = document) {
      root.querySelectorAll(sectionSelector).forEach((section) => {
        if (!section.dataset.renderedUrl) {
          section.dataset.renderedUrl = this.getCurrentUrl();
        }
      });
    },

    getSection(element = document) {
      return element.closest
        ? element.closest(sectionSelector)
        : document.querySelector(sectionSelector);
    },

    getMobileFilterTrigger(section) {
      return section.querySelector("[data-mobile-filter-trigger]");
    },

    getMobileFilterTriggers(section) {
      return Array.from(
        section.querySelectorAll("[data-mobile-filter-trigger]"),
      );
    },

    getMobileFilterSheet(section) {
      return section.querySelector("[data-mobile-filter-sheet]");
    },

    getMobileFilterPanel(section) {
      return section.querySelector(
        ".collection-product-grid__mobile-filter-panel",
      );
    },

    getMobileFilterFooter(section) {
      return section.querySelector(
        ".collection-product-grid__mobile-filter-footer",
      );
    },

    getMobileFilterPanelSkeletonMarkup() {
      const skeletonRows = Array.from({ length: 7 }, (_, index) => {
        const widthClass =
          index % 3 === 0
            ? "collection-product-grid__mobile-filter-skeleton-line--short"
            : index % 3 === 1
              ? "collection-product-grid__mobile-filter-skeleton-line--mid"
              : "";

        return `
          <div class="collection-product-grid__mobile-filter-skeleton-row">
            <span class="collection-product-grid__mobile-filter-skeleton-line skeleton ${widthClass}" aria-hidden="true"></span>
            <span class="collection-product-grid__mobile-filter-skeleton-icon skeleton" aria-hidden="true"></span>
          </div>
        `;
      }).join("");

      return `
        <div class="collection-product-grid__mobile-filter-skeleton" aria-hidden="true">
          <div class="collection-product-grid__mobile-filter-skeleton-pills">
            <span class="collection-product-grid__mobile-filter-skeleton-pill skeleton"></span>
            <span class="collection-product-grid__mobile-filter-skeleton-pill skeleton collection-product-grid__mobile-filter-skeleton-pill--short"></span>
            <span class="collection-product-grid__mobile-filter-skeleton-pill skeleton collection-product-grid__mobile-filter-skeleton-pill--mid"></span>
          </div>
          <div class="collection-product-grid__mobile-filter-skeleton-groups">
            ${skeletonRows}
          </div>
        </div>
      `;
    },

    showMobileFilterPanelSkeleton(section) {
      const panel = this.getMobileFilterPanel(section);

      if (!panel || panel.dataset.mobileFilterSkeleton === "true") {
        return;
      }

      this.mobileFilterPanelSnapshots.set(panel, panel.innerHTML);
      panel.style.setProperty(
        "--mobile-filter-panel-skeleton-min-height",
        `${Math.max(panel.clientHeight, 320)}px`,
      );
      panel.dataset.mobileFilterSkeleton = "true";
      panel.scrollTop = 0;
      panel.innerHTML = this.getMobileFilterPanelSkeletonMarkup();
    },

    restoreMobileFilterPanelSkeleton(section) {
      const panel = this.getMobileFilterPanel(section);

      if (!panel || panel.dataset.mobileFilterSkeleton !== "true") {
        return;
      }

      if (this.mobileFilterPanelSnapshots.has(panel)) {
        panel.innerHTML = this.mobileFilterPanelSnapshots.get(panel);
        this.mobileFilterPanelSnapshots.delete(panel);
      }

      delete panel.dataset.mobileFilterSkeleton;
      panel.style.removeProperty("--mobile-filter-panel-skeleton-min-height");
    },

    isInsideMobileFilterSheet(element) {
      return Boolean(
        element &&
          element.closest &&
          element.closest("[data-mobile-filter-sheet]"),
      );
    },

    getDesktopSidebar(section) {
      return section.querySelector(".collection-product-grid__sidebar");
    },

    updateMobileFilterButtonCompactWidth(mobileFilter, { force = false } = {}) {
      const button = mobileFilter.querySelector("[data-mobile-filter-trigger]");
      const viewportWidth = window.innerWidth;

      if (!button) {
        return;
      }

      if (
        !force &&
        mobileFilter.style.getPropertyValue(
          "--mobile-filter-button-compact-width",
        ) &&
        this.mobileFilterCompactWidthViewports.get(mobileFilter) ===
          viewportWidth
      ) {
        return;
      }

      const clone = button.cloneNode(true);

      clone.classList.remove("w-100");
      clone.style.position = "absolute";
      clone.style.visibility = "hidden";
      clone.style.pointerEvents = "none";
      clone.style.width = "max-content";
      clone.style.maxWidth = "none";
      clone.style.inset = "0 auto auto 0";

      mobileFilter.appendChild(clone);
      mobileFilter.style.setProperty(
        "--mobile-filter-button-compact-width",
        `${Math.ceil(clone.getBoundingClientRect().width)}px`,
      );
      this.mobileFilterCompactWidthViewports.set(mobileFilter, viewportWidth);
      clone.remove();
    },

    updateStickyMobileFilterBars(
      root = document,
      { forceCompactWidth = false } = {},
    ) {
      const isMobileFilterLayout =
        window.matchMedia("(max-width: 989px)").matches;

      root.querySelectorAll(sectionSelector).forEach((section) => {
        const mobileFilter = section.querySelector(
          "[data-mobile-filter-inline]",
        );

        if (!mobileFilter) {
          return;
        }

        if (!isMobileFilterLayout) {
          mobileFilter.classList.remove("is-sticky");
          return;
        }

        this.updateMobileFilterButtonCompactWidth(mobileFilter, {
          force: forceCompactWidth,
        });

        const isSticky = mobileFilter.classList.contains("is-sticky");
        const top = mobileFilter.getBoundingClientRect().top;
        const shouldBeSticky = isSticky ? top <= 6 : top <= 0;

        if (shouldBeSticky !== isSticky) {
          mobileFilter.classList.toggle("is-sticky", shouldBeSticky);
        }
      });
    },

    scheduleStickyMobileFilterBars(
      root = document,
      { forceCompactWidth = false } = {},
    ) {
      this.stickyMobileFilterRoot = root;
      this.stickyMobileFilterForceCompactWidth =
        this.stickyMobileFilterForceCompactWidth || forceCompactWidth;

      if (this.stickyMobileFilterFrame) {
        return;
      }

      this.stickyMobileFilterFrame = window.requestAnimationFrame(() => {
        const nextRoot = this.stickyMobileFilterRoot || document;
        const nextForceCompactWidth = this.stickyMobileFilterForceCompactWidth;

        this.stickyMobileFilterFrame = null;
        this.stickyMobileFilterRoot = null;
        this.stickyMobileFilterForceCompactWidth = false;
        this.updateStickyMobileFilterBars(nextRoot, {
          forceCompactWidth: nextForceCompactWidth,
        });
      });
    },

    initializeMobileFilterPills(root = document) {
      root
        .querySelectorAll("[data-mobile-filter-pills]")
        .forEach((container) => {
          const pillsList = container.querySelector(
            "[data-mobile-filter-pills-list]",
          );
          const expandButton = container.querySelector(
            "[data-mobile-filter-pills-expand]",
          );

          if (!pillsList || !expandButton) {
            return;
          }

          pillsList.appendChild(expandButton);
          expandButton.hidden = true;
          expandButton.setAttribute("aria-expanded", "false");

          const pillItems = Array.from(pillsList.children).filter(
            (element) =>
              element !== expandButton &&
              !element.classList.contains("collection-filter-pills__clear"),
          );

          pillItems.forEach((item) => {
            item.classList.remove("collection-filter-pills__pill--collapsed");
          });

          if (container.classList.contains("is-expanded")) {
            pillItems.forEach((item) => {
              item.classList.remove("collection-filter-pills__pill--collapsed");
            });
            expandButton.hidden = true;
            expandButton.setAttribute("aria-expanded", "true");
            return;
          }

          if (!window.matchMedia("(max-width: 749px)").matches) {
            expandButton.hidden = true;
            expandButton.setAttribute("aria-expanded", "false");
            return;
          }

          const getRowTops = (elements) =>
            Array.from(
              new Set(elements.map((element) => Math.round(element.offsetTop))),
            ).sort((a, b) => a - b);
          const getRowIndex = (element) =>
            getRowTops(
              Array.from(pillsList.children).filter(
                (child) =>
                  !child.classList.contains(
                    "collection-filter-pills__pill--collapsed",
                  ) && child.offsetParent !== null,
              ),
            ).indexOf(Math.round(element.offsetTop));

          const rowTops = getRowTops(pillItems);

          if (rowTops.length <= 3) {
            return;
          }

          const firstOverflowItem = pillItems.find(
            (item) => rowTops.indexOf(Math.round(item.offsetTop)) > 2,
          );

          if (!firstOverflowItem) {
            return;
          }

          expandButton.hidden = false;
          let visiblePillCount = pillItems.length;

          while (visiblePillCount >= 0) {
            pillItems.forEach((item, index) => {
              item.classList.toggle(
                "collection-filter-pills__pill--collapsed",
                index >= visiblePillCount,
              );
            });

            const lastVisiblePill = pillItems[visiblePillCount - 1];
            const nextSibling = lastVisiblePill
              ? lastVisiblePill.nextElementSibling
              : pillItems[0];

            pillsList.insertBefore(expandButton, nextSibling);

            const visibleItems = pillItems
              .slice(0, visiblePillCount)
              .concat(expandButton);
            const visibleRowTops = getRowTops(visibleItems);
            const expandButtonRowIndex = visibleRowTops.indexOf(
              Math.round(expandButton.offsetTop),
            );

            if (visibleRowTops.length <= 3 && expandButtonRowIndex <= 2) {
              break;
            }

            visiblePillCount -= 1;
          }

          expandButton.setAttribute("aria-expanded", "false");
        });
    },

    expandMobileFilterPills(button) {
      const container = button.closest("[data-mobile-filter-pills]");
      const section = this.getSection(button);

      if (!container) {
        return;
      }

      if (section) {
        this.collapseOpenMobileFilterSections(section);
      }

      container.classList.add("is-expanded");
      container
        .querySelectorAll(".collection-filter-pills__pill--collapsed")
        .forEach((pill) => {
          pill.classList.remove("collection-filter-pills__pill--collapsed");
        });
      button.hidden = true;
      button.setAttribute("aria-expanded", "true");
    },

    collapseOpenMobileFilterSections(section) {
      const sheet = this.getMobileFilterSheet(section);

      if (!sheet) {
        return;
      }

      sheet
        .querySelectorAll(".collection-filter-sidebar__section[open]")
        .forEach((filterSection) => {
          filterSection.removeAttribute("open");
        });
    },

    collapseMobileFilterPills(section) {
      section
        .querySelectorAll("[data-mobile-filter-pills]")
        .forEach((container) => {
          const expandButton = container.querySelector(
            "[data-mobile-filter-pills-expand]",
          );

          container.classList.remove("is-expanded");

          if (expandButton) {
            expandButton.setAttribute("aria-expanded", "false");
          }
        });

      this.initializeMobileFilterPills(section);
    },

    getSectionId(section) {
      return section && section.dataset ? section.dataset.sectionId : null;
    },

    getGtmSectionKey(section) {
      return section && section.dataset ? section.dataset.gtmSectionKey : null;
    },

    getCurrentFilterSectionKey(element) {
      const filterSection = element.closest("[data-filter-section-key]");
      return filterSection ? filterSection.dataset.filterSectionKey : null;
    },

    getFocusKey(element) {
      if (!element || !element.closest) {
        return null;
      }

      const focusTarget = element.closest("[data-focus-key]");

      if (focusTarget && focusTarget.closest("[data-mobile-filter-sheet]")) {
        return null;
      }

      return focusTarget ? focusTarget.dataset.focusKey : null;
    },

    getFilterInteraction(element) {
      if (
        element.matches(
          ".collection-filter-pills__clear, .collection-product-grid__mobile-filter-reset",
        )
      ) {
        return {
          action: "clear",
          selectedFilters: "All",
          selectedValues: "All",
        };
      }

      const filterKey = element.dataset.filterKey;
      const filterValue = element.dataset.filterValue;

      if (!filterKey || !filterValue) {
        return null;
      }

      return {
        action:
          element.classList.contains(
            "collection-filter-sidebar__checkbox--active",
          ) ||
          element.classList.contains(
            "collection-filter-sidebar__switch--active",
          ) ||
          element.classList.contains("collection-filter-pills__pill")
            ? "clear"
            : "apply",
        selectedFilters: filterKey,
        selectedValues: filterValue,
      };
    },

    emitFilterInteraction(interaction, nextSection, url) {
      if (!interaction) {
        return;
      }

      const interactions = [interaction].flat();
      const selected = {};
      interactions.forEach(({ selectedFilters, selectedValues }) => {
        selected[selectedFilters] = selected[selectedFilters] || [];
        if (!selected[selectedFilters].includes(selectedValues)) {
          selected[selectedFilters].push(selectedValues);
        }
      });
      const activeFilters = {};
      nextSection
        .querySelectorAll(
          ".collection-product-grid__active-filters .collection-filter-pills__pill[data-filter-key]",
        )
        .forEach((pill) => {
          const { filterKey, filterValue } = pill.dataset;
          activeFilters[filterKey] = activeFilters[filterKey] || [];
          activeFilters[filterKey].push(filterValue);
        });

      const activeFilterValues = Object.values(activeFilters);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "filter-interaction",
        user: window.tracking?.userData,
        user_data: window.tracking?.additionalUserData,
        filter_action: interactions[interactions.length - 1].action,
        selected_filters: Object.keys(selected).join(";"),
        selected_values: Object.values(selected)
          .map((values) => values.join(","))
          .join(";"),
        active_filters: Object.entries(activeFilters)
          .map(
            ([filterKey, filterValues]) =>
              `${filterKey}:${filterValues.join(",")};`,
          )
          .join(""),
        number_active_filters: activeFilterValues.reduce(
          (count, filterValues) => count + filterValues.length,
          0,
        ),
        collection_type: new URL(url, window.location.origin).pathname
          .split("/")
          .pop(),
        number_filter_results: Number(
          nextSection.querySelector(".collection-product-grid__count span")
            ?.textContent,
        ),
      });
    },

    getMobileFocusKey(element) {
      if (!element || !element.closest) {
        return null;
      }

      const focusTarget = element.closest("[data-focus-key]");

      return focusTarget ? focusTarget.dataset.focusKey : null;
    },

    restoreFocus(section, focusKey) {
      const selector = focusKey
        ? `[data-focus-key="${focusKey}"]`
        : "[data-focus-fallback]";
      const target =
        section.querySelector(selector) ||
        section.querySelector("[data-focus-fallback]");

      if (!target || typeof target.focus !== "function") {
        return;
      }

      window.requestAnimationFrame(() => {
        target.focus({ preventScroll: true });
      });
    },

    getNextOpenFilterSectionKeys(section, explicitKeys = []) {
      const requestedKeys = (explicitKeys || []).filter(Boolean);

      if (requestedKeys.length > 0) {
        return requestedKeys;
      }

      return Array.from(
        section.querySelectorAll(".collection-filter-sidebar__section[open]"),
      )
        .map((filterSection) => filterSection.dataset.filterSectionKey)
        .filter(Boolean);
    },

    restoreFilterSectionState(section, keys) {
      const keysToOpen = new Set((keys || []).filter(Boolean));

      this.suppressMobileFilterToggleCommit = true;
      section
        .querySelectorAll(".collection-filter-sidebar__section")
        .forEach((filterSection) => {
          if (keysToOpen.has(filterSection.dataset.filterSectionKey)) {
            filterSection.setAttribute("open", "");
          } else {
            filterSection.removeAttribute("open");
          }
        });
      window.setTimeout(() => {
        this.suppressMobileFilterToggleCommit = false;
      }, 0);
    },

    handleToggle(event) {
      const details = event.target;

      if (
        !details ||
        !(details instanceof HTMLDetailsElement) ||
        !details.matches(".collection-filter-sidebar__section")
      ) {
        return;
      }

      const section = this.getSection(details);

      if (!section) {
        return;
      }

      if (details.open) {
        const filterSidebar = details.closest(".collection-filter-sidebar");

        if (filterSidebar) {
          filterSidebar
            .querySelectorAll(".collection-filter-sidebar__section[open]")
            .forEach((filterSection) => {
              if (filterSection !== details) {
                filterSection.removeAttribute("open");
              }
            });
        }
      }

      if (this.suppressMobileFilterToggleCommit) {
        return;
      }

      if (!this.isInsideMobileFilterSheet(details)) {
        return;
      }

      const filterSectionKey = details.dataset.filterSectionKey;

      this.commitStagedMobileFilter(section, {
        closeSheet: false,
        focusKey: this.getMobileFocusKey(details),
        keepMobileFilterOpen: true,
        openFilterSectionKeys:
          details.open && filterSectionKey ? [filterSectionKey] : [],
      });
    },

    getFocusableElements(container) {
      return Array.from(
        container.querySelectorAll(
          [
            "a[href]",
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            '[tabindex]:not([tabindex="-1"])',
          ].join(", "),
        ),
      ).filter(
        (element) =>
          element.offsetParent !== null || element === document.activeElement,
      );
    },

    focusMobileFilter(section) {
      const sheet = this.getMobileFilterSheet(section);

      if (!sheet) {
        return;
      }

      const focusableElements = this.getFocusableElements(sheet);
      const firstFocusable = focusableElements[0];

      if (!firstFocusable || typeof firstFocusable.focus !== "function") {
        return;
      }

      window.requestAnimationFrame(() => {
        firstFocusable.focus({ preventScroll: true });
      });
    },

    restoreMobileFilterFocus(section, focusKey) {
      if (!focusKey) {
        return;
      }

      const sheet = this.getMobileFilterSheet(section);
      const target = sheet
        ? sheet.querySelector(`[data-focus-key="${focusKey}"]`)
        : null;

      if (!target || typeof target.focus !== "function") {
        return;
      }

      window.requestAnimationFrame(() => {
        target.focus({ preventScroll: true });
      });
    },

    getElementsByDataValue(container, attributeName, value) {
      if (!container || !value) {
        return [];
      }

      return Array.from(
        container.querySelectorAll(`[${attributeName}]`),
      ).filter((element) => element.getAttribute(attributeName) === value);
    },

    getMobileFilterAnchorCandidates(panel, focusKey) {
      const candidates = [];

      if (!panel) {
        return candidates;
      }

      const addCandidate = (type, key, element) => {
        if (!key || !element) {
          return;
        }

        candidates.push({
          key,
          top: element.getBoundingClientRect().top,
          type,
        });
      };

      const focusedElement = this.getElementsByDataValue(
        panel,
        "data-focus-key",
        focusKey,
      )[0];
      const focusedSection = focusedElement
        ? focusedElement.closest("[data-filter-section-key]")
        : null;

      addCandidate("focus", focusKey, focusedElement);

      if (focusedSection) {
        addCandidate(
          "filterSection",
          focusedSection.dataset.filterSectionKey,
          focusedSection,
        );
      }

      const panelRect = panel.getBoundingClientRect();
      const visibleSections = Array.from(
        panel.querySelectorAll(".collection-filter-sidebar__section"),
      )
        .map((filterSection) => ({
          element: filterSection,
          rect: filterSection.getBoundingClientRect(),
        }))
        .filter(
          ({ rect }) =>
            rect.bottom > panelRect.top && rect.top < panelRect.bottom,
        )
        .sort(
          (first, second) =>
            Math.abs(first.rect.top - panelRect.top) -
            Math.abs(second.rect.top - panelRect.top),
        );

      if (visibleSections[0]) {
        addCandidate(
          "filterSection",
          visibleSections[0].element.dataset.filterSectionKey,
          visibleSections[0].element,
        );
      }

      return candidates;
    },

    getMobileFilterAnchorElement(panel, candidate) {
      if (!panel || !candidate) {
        return null;
      }

      if (candidate.type === "focus") {
        return this.getElementsByDataValue(
          panel,
          "data-focus-key",
          candidate.key,
        )[0];
      }

      if (candidate.type === "filterSection") {
        return this.getElementsByDataValue(
          panel,
          "data-filter-section-key",
          candidate.key,
        )[0];
      }

      return null;
    },

    restoreMobileFilterPanelPosition(panel, candidates, fallbackScrollTop) {
      if (!panel) {
        return;
      }

      const anchor = (candidates || [])
        .map((candidate) => ({
          candidate,
          element: this.getMobileFilterAnchorElement(panel, candidate),
        }))
        .find(({ element }) => element);

      if (!anchor) {
        panel.scrollTop = fallbackScrollTop;
        return;
      }

      const nextTop = anchor.element.getBoundingClientRect().top;

      panel.scrollTop += nextTop - anchor.candidate.top;
    },

    restoreMobileFilterPanelScrollPosition(
      panel,
      candidates,
      previousScrollTop,
    ) {
      if (!panel) {
        return;
      }

      if (Number.isFinite(previousScrollTop)) {
        const maxScrollTop = Math.max(
          0,
          panel.scrollHeight - panel.clientHeight,
        );
        const nextScrollTop = Math.min(previousScrollTop, maxScrollTop);

        panel.scrollTop = nextScrollTop;

        if (Math.abs(panel.scrollTop - previousScrollTop) <= 1) {
          return;
        }
      }

      this.restoreMobileFilterPanelPosition(
        panel,
        candidates,
        previousScrollTop,
      );
    },

    getFirstMobileFilterSectionKey(panel) {
      const firstFilterSection = panel
        ? Array.from(
            panel.querySelectorAll(".collection-filter-sidebar__section"),
          ).find((filterSection) => filterSection.offsetParent !== null)
        : null;

      return firstFilterSection
        ? firstFilterSection.dataset.filterSectionKey
        : null;
    },

    shouldRestoreToFirstMobileFilterSection(focusKey) {
      return typeof focusKey === "string" && focusKey.startsWith("pill-");
    },

    restoreMobileFilterAccordionPosition(panel, filterSectionKey) {
      if (!panel || !filterSectionKey) {
        return false;
      }

      const filterSection = this.getElementsByDataValue(
        panel,
        "data-filter-section-key",
        filterSectionKey,
      )[0];

      if (!filterSection) {
        return false;
      }

      const summary =
        filterSection.querySelector(".collection-filter-sidebar__summary") ||
        filterSection;
      const panelTop = panel.getBoundingClientRect().top;
      const summaryTop = summary.getBoundingClientRect().top;

      panel.scrollTop += summaryTop - panelTop;

      if (typeof summary.focus === "function") {
        summary.focus({ preventScroll: true });
      }

      return true;
    },

    isMobileFilterPillsExpanded(section) {
      const sheet = this.getMobileFilterSheet(section);
      const pills = sheet
        ? sheet.querySelector("[data-mobile-filter-pills]")
        : null;

      return Boolean(pills && pills.classList.contains("is-expanded"));
    },

    restoreMobileFilterPillsExpandedState(root, wasExpanded) {
      if (!wasExpanded) {
        return;
      }

      const sheet =
        root && root.matches && root.matches("[data-mobile-filter-sheet]")
          ? root
          : this.getMobileFilterSheet(root);
      const pills = sheet
        ? sheet.querySelector("[data-mobile-filter-pills]")
        : null;

      if (pills) {
        pills.classList.add("is-expanded");
      }
    },

    cancelStagedMobileFilterRequest() {
      this.mobileFilterRenderRequestId += 1;

      if (this.mobileFilterAbortController) {
        this.mobileFilterAbortController.abort();
        this.mobileFilterAbortController = null;
      }
    },

    runSectionScripts(section) {
      section.querySelectorAll("script").forEach((script) => {
        const nextScript = document.createElement("script");

        Array.from(script.attributes).forEach((attribute) => {
          nextScript.setAttribute(attribute.name, attribute.value);
        });

        nextScript.textContent = script.textContent;
        script.replaceWith(nextScript);
      });
    },

    refreshSectionGtm(section) {
      const gtmSectionKey = this.getGtmSectionKey(section);

      if (window.tracking && gtmSectionKey) {
        try {
          if (typeof window.tracking.resetKeyedEcomData === "function") {
            window.tracking.resetKeyedEcomData(gtmSectionKey);
          }
        } catch (error) {
          console.warn(
            "Collection grid GTM reset failed after async render.",
            error,
          );
        }
      }

      this.runSectionScripts(section);

      if (window.plantura && window.plantura.gtm) {
        try {
          if (
            typeof window.plantura.gtm.selectItemEventListener === "function"
          ) {
            window.plantura.gtm.selectItemEventListener();
          }
        } catch (error) {
          console.warn(
            "Collection grid select_item listener refresh failed.",
            error,
          );
        }

        try {
          if (
            gtmSectionKey &&
            typeof window.plantura.gtm.viewItemListObserver === "function"
          ) {
            window.plantura.gtm.viewItemListObserver(true, gtmSectionKey);
          }
        } catch (error) {
          console.warn("Collection grid view_item_list refresh failed.", error);
        }
      }
    },

    openMobileFilter(section, { restoreFocus = true } = {}) {
      const triggers = this.getMobileFilterTriggers(section);
      const sheet = this.getMobileFilterSheet(section);

      if (triggers.length === 0 || !sheet) {
        return;
      }

      window.clearTimeout(this.mobileFilterHideTimer);
      triggers.forEach((trigger) => {
        trigger.setAttribute("aria-expanded", "true");
      });
      sheet.hidden = false;
      sheet.classList.remove("is-open");
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          sheet.classList.add("is-open");
          this.initializeMobileFilterPills(section);
          if (restoreFocus) {
            this.focusMobileFilter(section);
          }
        });
      });
      document.body.classList.add("noscroll");
    },

    closeMobileFilter(section) {
      const triggers = this.getMobileFilterTriggers(section);
      const sheet = this.getMobileFilterSheet(section);

      if (triggers.length === 0 || !sheet) {
        return;
      }

      triggers.forEach((trigger) => {
        trigger.setAttribute("aria-expanded", "false");
      });
      this.cancelStagedMobileFilterRequest();
      sheet.classList.remove("is-updating");
      sheet.removeAttribute("aria-busy");
      this.restoreMobileFilterPanelSkeleton(section);
      this.collapseOpenMobileFilterSections(section);
      this.collapseMobileFilterPills(section);
      delete sheet.dataset.stagedUrl;
      sheet.classList.remove("is-open");
      window.clearTimeout(this.mobileFilterHideTimer);
      this.mobileFilterHideTimer = window.setTimeout(() => {
        sheet.hidden = true;
      }, 260);
      document.body.classList.remove("noscroll");

      if (
        this.lastMobileFilterTrigger &&
        document.contains(this.lastMobileFilterTrigger) &&
        typeof this.lastMobileFilterTrigger.focus === "function"
      ) {
        this.lastMobileFilterTrigger.focus({ preventScroll: true });
      }
    },

    commitStagedMobileFilter(
      section,
      {
        closeSheet = true,
        focusKey = "mobile-filter-trigger",
        keepMobileFilterOpen = false,
        openFilterSectionKeys = [],
      } = {},
    ) {
      const sheet = this.getMobileFilterSheet(section);
      const stagedUrl = sheet && sheet.dataset.stagedUrl;

      if (!stagedUrl) {
        if (closeSheet) {
          this.closeMobileFilter(section);
        }
        return false;
      }

      this.cancelStagedMobileFilterRequest();
      delete sheet.dataset.stagedUrl;

      if (closeSheet) {
        this.closeMobileFilter(section);
      } else if (keepMobileFilterOpen) {
        sheet.classList.add("is-updating");
        sheet.setAttribute("aria-busy", "true");
        this.showMobileFilterPanelSkeleton(section);
      }

      this.renderSection(section, stagedUrl, {
        focusKey,
        keepMobileFilterOpen,
        openFilterSectionKeys,
        pushState: true,
        filterInteraction: sheet.filterInteraction,
      });
      delete sheet.filterInteraction;

      return true;
    },

    handleResize() {
      this.initializeMobileFilterPills();
      this.scheduleStickyMobileFilterBars(document, {
        forceCompactWidth: true,
      });
    },

    handleScroll() {
      this.scheduleStickyMobileFilterBars();
    },

    handleClick(event) {
      const button = event.target.closest("button");
      const buttonSection = button ? this.getSection(button) : null;

      if (button && button.matches("[data-mobile-filter-pills-expand]")) {
        event.preventDefault();
        this.expandMobileFilterPills(button);
        return;
      }

      if (
        button &&
        buttonSection &&
        button.matches("[data-mobile-filter-trigger]")
      ) {
        event.preventDefault();
        this.lastMobileFilterTrigger = button;
        this.openMobileFilter(buttonSection);
        return;
      }

      if (
        button &&
        buttonSection &&
        button.matches("[data-mobile-filter-apply]")
      ) {
        event.preventDefault();
        this.commitStagedMobileFilter(buttonSection);
        return;
      }

      if (
        button &&
        buttonSection &&
        button.matches("[data-mobile-filter-close]")
      ) {
        event.preventDefault();
        this.commitStagedMobileFilter(buttonSection);
        return;
      }

      const link = event.target.closest("a");

      if (!link) {
        return;
      }

      const section = this.getSection(link);

      if (!section || !link.matches(interactiveSelectors)) {
        return;
      }

      const currentFilterSectionKey = this.getCurrentFilterSectionKey(link);
      const focusKey = this.getFocusKey(link);
      const keepMobileFilterOpen = this.isInsideMobileFilterSheet(link);
      const filterInteraction = this.getFilterInteraction(link);

      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        link.target === "_blank" ||
        link.hasAttribute("download")
      ) {
        return;
      }

      event.preventDefault();
      if (keepMobileFilterOpen) {
        this.stageMobileFilterSection(section, link.href, {
          filterInteraction,
          openFilterSectionKeys: currentFilterSectionKey
            ? [currentFilterSectionKey]
            : [],
          focusKey: this.getMobileFocusKey(link),
        });
        return;
      }

      if (!keepMobileFilterOpen) {
        this.closeMobileFilter(section);
      }
      this.renderSection(section, link.href, {
        pushState: true,
        openFilterSectionKeys: currentFilterSectionKey
          ? [currentFilterSectionKey]
          : [],
        filterInteraction,
        focusKey,
        keepMobileFilterOpen,
      });
    },

    handleSubmit(event) {
      const form = event.target;
      const section = this.getSection(form);

      if (!section || !form.matches(formSelectors)) {
        return;
      }

      event.preventDefault();

      const nextUrl = new URL(
        form.action || window.location.pathname,
        window.location.origin,
      );
      const formData = event.submitter
        ? new FormData(form, event.submitter)
        : new FormData(form);
      const searchParams = new URLSearchParams();

      for (const [key, value] of formData.entries()) {
        if (value === "" || value == null) {
          continue;
        }

        searchParams.append(key, value);
      }

      nextUrl.search = searchParams.toString();

      const currentFilterSectionKey = this.getCurrentFilterSectionKey(form);
      const focusKey = this.getFocusKey(event.submitter || form);
      const keepMobileFilterOpen = this.isInsideMobileFilterSheet(form);
      const priceInputs = Array.from(
        form.querySelectorAll("input[type=number]"),
      );
      const filterInteraction = priceInputs.length
        ? {
            action: "apply",
            selectedFilters: this.getCurrentFilterSectionKey(form),
            selectedValues: priceInputs
              .map((input) => input.value)
              .filter(Boolean)
              .join(" - "),
          }
        : event.submitter?.name === "sort_by"
          ? {
              action: "apply",
              selectedFilters: form.dataset.filterKey,
              selectedValues: event.submitter.value,
            }
          : null;

      if (keepMobileFilterOpen) {
        this.stageMobileFilterSection(section, nextUrl.toString(), {
          filterInteraction,
          openFilterSectionKeys: currentFilterSectionKey
            ? [currentFilterSectionKey]
            : [],
          focusKey: this.getMobileFocusKey(event.submitter || form),
        });
        return;
      }

      if (!keepMobileFilterOpen) {
        this.closeMobileFilter(section);
      }
      this.renderSection(section, nextUrl.toString(), {
        pushState: true,
        openFilterSectionKeys: currentFilterSectionKey
          ? [currentFilterSectionKey]
          : [],
        filterInteraction,
        focusKey,
        keepMobileFilterOpen,
      });
    },

    handleKeydown(event) {
      if (event.key !== "Escape" && event.key !== "Tab") {
        return;
      }

      const openSection = Array.from(
        document.querySelectorAll(sectionSelector),
      ).find((section) => {
        const sheet = this.getMobileFilterSheet(section);
        return sheet && !sheet.hidden;
      });

      if (!openSection) {
        return;
      }

      if (event.key === "Tab") {
        const sheet = this.getMobileFilterSheet(openSection);
        const focusableElements = sheet ? this.getFocusableElements(sheet) : [];
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        if (!firstFocusable || !lastFocusable) {
          event.preventDefault();
          return;
        }

        if (event.shiftKey && document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
          return;
        }

        if (!event.shiftKey && document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }

        return;
      }

      this.commitStagedMobileFilter(openSection);
    },

    handlePopState() {
      const section = document.querySelector(sectionSelector);

      if (!section) {
        return;
      }

      if (section.dataset.renderedUrl === this.getCurrentUrl()) {
        return;
      }

      this.closeMobileFilter(section);
      this.renderSection(section, window.location.href);
    },

    async renderSection(
      section,
      url,
      {
        pushState = false,
        openFilterSectionKeys = [],
        focusKey = null,
        keepMobileFilterOpen = false,
        filterInteraction = null,
      } = {},
    ) {
      const nextUrl = new URL(url, window.location.origin);
      const fetchUrl = new URL(nextUrl.toString());
      const sectionId = section.dataset.sectionId;

      if (!sectionId) {
        return;
      }

      fetchUrl.searchParams.delete("section_id");
      fetchUrl.searchParams.set("section_id", sectionId);

      if (this.abortController) {
        this.abortController.abort();
      }

      const requestId = this.renderRequestId + 1;
      const abortController = new AbortController();

      this.renderRequestId = requestId;
      this.abortController = abortController;
      if (!keepMobileFilterOpen) {
        this.closeMobileFilter(section);
      }
      section.classList.add("is-loading");
      section.setAttribute("aria-busy", "true");

      try {
        const response = await fetch(fetchUrl.toString(), {
          signal: abortController.signal,
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Collection section request failed: ${response.status}`,
          );
        }

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const nextSection = parsed.querySelector(sectionSelector);

        if (requestId !== this.renderRequestId) {
          return;
        }

        if (!nextSection) {
          throw new Error("Updated collection section markup was not found.");
        }

        const nextOpenFilterSectionKeys = this.getNextOpenFilterSectionKeys(
          section,
          openFilterSectionKeys,
        );

        this.restoreFilterSectionState(nextSection, nextOpenFilterSectionKeys);
        if (pushState) {
          window.history.pushState(
            {},
            "",
            `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`,
          );
        }

        nextSection.dataset.renderedUrl = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        section.replaceWith(nextSection);
        this.initializeSectionState(nextSection.parentNode || document);
        this.initializeMobileFilterPills(nextSection);
        this.updateStickyMobileFilterBars(nextSection.parentNode || document);
        if (keepMobileFilterOpen) {
          this.openMobileFilter(nextSection);
        }
        this.refreshSectionGtm(nextSection);
        this.emitFilterInteraction(filterInteraction, nextSection, nextUrl);
        if (!keepMobileFilterOpen) {
          this.restoreFocus(nextSection, focusKey);
          if (pushState) {
            window.requestAnimationFrame(() => {
              nextSection.scrollIntoView({ block: "start" });
            });
          }
        }

        if (window.plantura && window.plantura.event) {
          window.plantura.event.emit("collection-product-grid:updated", {
            sectionId,
            url: nextUrl.toString(),
          });
        }
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        if (requestId !== this.renderRequestId) {
          return;
        }

        console.error(
          "Unable to update collection grid asynchronously.",
          error,
        );
        window.location.href = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      } finally {
        if (requestId !== this.renderRequestId) {
          return;
        }

        if (this.abortController === abortController) {
          this.abortController = null;
        }

        section.classList.remove("is-loading");
        section.removeAttribute("aria-busy");
      }
    },

    async stageMobileFilterSection(
      section,
      url,
      {
        filterInteraction = null,
        openFilterSectionKeys = [],
        focusKey = null,
      } = {},
    ) {
      const sheet = this.getMobileFilterSheet(section);
      const panel = this.getMobileFilterPanel(section);
      const footer = this.getMobileFilterFooter(section);
      const nextUrl = new URL(url, window.location.origin);
      const fetchUrl = new URL(nextUrl.toString());
      const sectionId = section.dataset.sectionId;

      if (!sectionId || !sheet || !panel || !footer) {
        return;
      }

      fetchUrl.searchParams.delete("section_id");
      fetchUrl.searchParams.set("section_id", sectionId);

      if (this.mobileFilterAbortController) {
        this.mobileFilterAbortController.abort();
      }

      const requestId = this.mobileFilterRenderRequestId + 1;
      const abortController = new AbortController();
      const nextOpenFilterSectionKeys = this.getNextOpenFilterSectionKeys(
        section,
        openFilterSectionKeys,
      );
      const panelScrollTop = panel.scrollTop;
      const anchorCandidates = this.getMobileFilterAnchorCandidates(
        panel,
        focusKey,
      );
      const wasPillsExpanded = this.isMobileFilterPillsExpanded(section);
      const restoreToFirstFilterSection =
        this.shouldRestoreToFirstMobileFilterSection(focusKey);

      this.mobileFilterRenderRequestId = requestId;
      this.mobileFilterAbortController = abortController;
      sheet.dataset.stagedUrl = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      if (filterInteraction) {
        sheet.filterInteraction = [
          ...(filterInteraction.selectedFilters === "All"
            ? []
            : sheet.filterInteraction || []),
          filterInteraction,
        ];
      }
      sheet.classList.add("is-updating");
      sheet.setAttribute("aria-busy", "true");
      this.showMobileFilterPanelSkeleton(section);

      try {
        const response = await fetch(fetchUrl.toString(), {
          signal: abortController.signal,
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Collection mobile filter request failed: ${response.status}`,
          );
        }

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const nextSection = parsed.querySelector(sectionSelector);

        if (requestId !== this.mobileFilterRenderRequestId) {
          return;
        }

        if (!nextSection) {
          throw new Error("Updated collection section markup was not found.");
        }

        const nextSheet = this.getMobileFilterSheet(nextSection);
        const nextPanel = this.getMobileFilterPanel(nextSection);
        const nextFooter = this.getMobileFilterFooter(nextSection);

        if (!nextSheet || !nextPanel || !nextFooter) {
          throw new Error("Updated mobile filter markup was not found.");
        }

        this.restoreFilterSectionState(nextSheet, nextOpenFilterSectionKeys);
        this.restoreMobileFilterPillsExpandedState(nextSheet, wasPillsExpanded);
        this.mobileFilterPanelSnapshots.delete(panel);
        panel.replaceWith(nextPanel);
        footer.replaceWith(nextFooter);

        const updatedPanel = this.getMobileFilterPanel(section);
        const updatedSheet = this.getMobileFilterSheet(section);

        if (updatedSheet) {
          updatedSheet.dataset.stagedUrl = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        }

        this.initializeMobileFilterPills(section);
        const restoreFilterSectionKey =
          nextOpenFilterSectionKeys[0] ||
          (restoreToFirstFilterSection
            ? this.getFirstMobileFilterSectionKey(updatedPanel)
            : null);
        const didRestoreAccordion = this.restoreMobileFilterAccordionPosition(
          updatedPanel,
          restoreFilterSectionKey,
        );

        if (!didRestoreAccordion) {
          this.restoreMobileFilterPanelScrollPosition(
            updatedPanel,
            anchorCandidates,
            panelScrollTop,
          );
          this.restoreMobileFilterFocus(section, focusKey);
        }
        document.body.classList.add("noscroll");
      } catch (error) {
        if (error.name === "AbortError") {
          if (requestId === this.mobileFilterRenderRequestId) {
            this.restoreMobileFilterPanelSkeleton(section);
          }
          return;
        }

        if (requestId !== this.mobileFilterRenderRequestId) {
          return;
        }

        this.restoreMobileFilterPanelSkeleton(section);
        console.error(
          "Unable to stage collection mobile filters asynchronously.",
          error,
        );
        window.location.href = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      } finally {
        if (requestId !== this.mobileFilterRenderRequestId) {
          return;
        }

        if (this.mobileFilterAbortController === abortController) {
          this.mobileFilterAbortController = null;
        }

        const currentSheet = this.getMobileFilterSheet(section);

        if (currentSheet) {
          currentSheet.classList.remove("is-updating");
          currentSheet.removeAttribute("aria-busy");
        }
      }
    },
  };

  window.PlanturaCollectionProductGrid = CollectionProductGrid;
  CollectionProductGrid.init();
})();
